import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';
import { connect } from 'react-redux';

import { supportUrl } from 'Config/lang';
import { submitRating, submitText, getFeedback } from 'Redux/ducks/feedback';
import 'Components/ProductReviews/UserReviews/UserReviewStars.scss';

import 'Components/Order/Feedback/Feedback.scss';

const messages = defineMessages({
  awful: {
    id: 'order.feedback.awful',
    defaultMessage: 'Awful',
  },
  disappointing: {
    id: 'order.feedback.disappointing',
    defaultMessage: 'A bit disappointing.',
  },
  ok: {
    id: 'order.feedback.ok',
    defaultMessage: 'Ok, but nothing special.',
  },
  good: {
    id: 'order.feedback.good',
    defaultMessage: 'Pretty Good.',
  },
  amazing: {
    id: 'order.feedback.amazing',
    defaultMessage: 'Amazing!',
  },
  pleasetypehere: {
    id: 'order.feedback.pleasetypehere',
    defaultMessage: 'Please type here',
  },
});


const Feedback = ({
  productId,
  orderId,
  productName,
  siteTheme,
}) => {
  const [rating, setRating] = useState(undefined);
  const [ratingName, setRatingName] = useState(undefined);
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { formatMessage } = useIntl();
  const [textLengthError, setTextLengthError] = useState(false);

  const submitRatingClick = async (value) => {
    const result = await submitRating({
      rating: value,
      productId,
      orderId,
    });

    setRating(result);
  };

  const submitTextClick = async (value) => {
    if (text && text.length < 10) {
      return setTextLengthError(true);
    }

    await submitText({
      rating: value,
      productId,
      orderId,
      text,
    });

    return setSubmitted(true);
  };

  const onInputChange = (value) => {
    if (textLengthError) setTextLengthError(false);
    setText(value);
  };

  useEffect(() => {
    const loadFeedback = async () => {
      const result = await getFeedback(productId);

      if (result && result.rating) setRating(result.rating);
      if (result && result.text) setText(result.text);
    };

    loadFeedback();
  }, [productId]);

  const changeRatingName = (score) => {
    let name;
    switch (score) {
      case 1:
        name = formatMessage(messages.awful);
        break;
      case 2:
        name = formatMessage(messages.disappointing);
        break;
      case 3:
        name = formatMessage(messages.ok);
        break;
      case 4:
        name = formatMessage(messages.good);
        break;
      case 5:
        name = formatMessage(messages.amazing);
        break;
      default:
        name = undefined;
    }

    setRatingName(name);
  };

  const starRatingButtons = () => {
    const starButtons = [...Array(5).keys()].map((score, index) =>
      <button
        className={classNames('star-rating-button', `star-${index + 1}`, { 'highlighted-star': (index + 1) <= rating })}
        key={score}
        aria-label="Star rating"
        onClick={() => submitRatingClick(index + 1)}
        onMouseMove={() => changeRatingName(score + 1)}
        onMouseLeave={() => setRatingName(undefined)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 90" preserveAspectRatio="xMinYMid meet" role="img" aria-label="User review star">
          <polygon points="45 0 58.91 29.63 90 34.38 67.5 57.44 72.81 90 45 74.63 17.19 90 22.5 57.44 0 34.38 31.09 29.63 45 0" />
        </svg>
      </button>,
    );

    return starButtons;
  };

  const lightTheme = siteTheme === 'light';

  return (
    <section className="Feedback">
      <div className="Feedback__title h3 text-center">
        {!submitted ?
          <FormattedMessage
            id="order.feedback.title"
            defaultMessage="How would you rate the contents of your {productName} ?"
            values={{ productName }}
          />
          :
          <FormattedMessage
            id="order.feedback.titlesubmitted"
            defaultMessage="Thank you for your feedback!"
          />
        }
      </div>
      <div className="Feedback__message text-center">
        {!submitted ?
          <FormattedMessage
            id="order.feedback.message"
            defaultMessage="We are continually working to make our Bundles better! Help us by giving your feedback."
            values={{ productName }}
          />
          :
          <FormattedMessage
            id="order.feedback.messagesubmitted"
            defaultMessage="Thank you for taking the time to rate your bundle. You can come back and amend your rating at any time."
          />
        }
      </div>
      <div className={classNames('star-rating-buttons-container', { 'stars-light-theme': lightTheme })}>
        {starRatingButtons(rating)}
      </div>

      <div className="Feedback_ratingName">
        {ratingName}
      </div>

      {rating > 0 && !submitted &&
        <React.Fragment>
          <div className="form-group text-center">
            <label htmlFor="feedbackText" className="col-form-label col-form-label-lg h4">
              <FormattedMessage id="order.feedback.whatcouldwedoto" defaultMessage="What could we do to improve?" />
            </label>
            <textarea
              id="feedbackText"
              name="text"
              type="text"
              rows="3"
              className="form-control p-2"
              maxLength="5000"
              minLength="30"
              aria-label="Feedback Text"
              value={text}
              autoComplete="off"
              placeholder={formatMessage(messages.pleasetypehere)}
              onChange={e => onInputChange(e.target.value)}
            />
          </div>
          {textLengthError &&
            <div className="invalid-field">
              <FormattedMessage
                id="order.feedback.yourreviewmustbemore"
                defaultMessage="Your review must be more than 10 characters"
              />
            </div>
          }

          <div className="form-group text-center text-uppercase form-button-container">
            <button
              className="btn btn-primary submit-button"
              onClick={() => submitTextClick()}
            >
              <FormattedMessage
                id="order.feedback.submit"
                defaultMessage="Submit"
              />
            </button>
          </div>
        </React.Fragment>
      }

      <div className="Feedback__supportMessage text-muted">
        <FormattedMessage
          id="order.feedback.anyproblemswithyouorder"
          defaultMessage="If you have any problems with your order please contact our friendly"
        />
        <span>
          &nbsp;
          <a
            href={`${supportUrl()}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id="order.feedback.supportteam" defaultMessage="Support Team." />
          </a>
        </span>
      </div>
    </section>
  );
};

Feedback.propTypes = {
  productId: PropTypes.string,
  orderId: PropTypes.string,
  productName: PropTypes.string,
  siteTheme: PropTypes.string,
};

Feedback.defaultProps = {
  productId: '',
  orderId: '',
  userId: '',
  productName: '',
  siteTheme: 'dark',
};

const mapStateToProps = ({
  siteTheme,
}) => ({
  siteTheme,
});


export default connect(mapStateToProps)(Feedback);
